/**
 * companion.ts — daily greeting + surprise + photos + taught phrases +
 * wish letter + voice bottle + onboarding hatch (PR-15 split of api/client.ts).
 *
 * Holds the Wave-B "companion" surfaces — everything that's about the
 * relationship with the pet but isn't the chat stream proper. Onboarding
 * hatch lives here too because it produces the kid's first pet via the
 * same companion-flavoured API namespace.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by HomeOverlays (greeting / surprise / wish letter),
 *     ChatPage (photos + phrases), VoiceBottleModal, OnboardingHatchPage,
 *     OnboardingQuizPage.
 *   - Backend: backend/src/routes/companion.js, onboarding.js.
 *
 * 关键约束：
 *   - greeting/dream/wish-letter forward `?lang=` so the AI-generated
 *     copy is already in the right language on arrival.
 *   - voiceBottle truncates audio_b64 / mime to undefined when missing so
 *     the JSON body stays small for text-only bottles.
 */
import { call, getLang } from './_core';

export const companionApi = {
  // ─── Companion (Wave B) ───────────────────────────────────────────────
  greetingDaily() {
    return call<{
      greeting: { id: number; content: string; day_key: string; created_at: number; seen_at: number | null } | null;
      reason?: string;
    }>(`/api/v1/child/companion/greeting?lang=${getLang()}`);
  },
  greetingMarkSeen(id: number) {
    return call<{ ok: true }>(`/api/v1/child/companion/greeting/${id}/seen`, { method: 'POST' });
  },
  surpriseGet() {
    return call<{
      surprise: {
        id: number; amount: number; message: string; away_ms: number;
        created_at: number; status: 'pending';
      } | null;
    }>(`/api/v1/child/companion/surprise?lang=${getLang()}`);
  },
  surpriseClaim(id: number) {
    return call<{ ok: true; amount?: number; already_claimed?: boolean }>(
      `/api/v1/child/companion/surprise/${id}/claim`, { method: 'POST' });
  },
  photosList(limit = 50) {
    return call<{
      photos: Array<{
        id: number; scene: string; caption: string | null;
        sticker: string | null; pet_emoji: string | null;
        created_at: number; metadata: unknown;
      }>;
    }>(`/api/v1/child/companion/photos?limit=${limit}`);
  },
  photosCreate(payload: {
    scene: string; caption?: string | null; sticker?: string | null;
    pet_emoji?: string | null; metadata?: Record<string, unknown>;
  }) {
    return call<{ ok: true; id: number; created_at: number }>(
      '/api/v1/child/companion/photos',
      { method: 'POST', body: JSON.stringify(payload) });
  },
  photosDelete(id: number) {
    return call<{ ok: true }>(`/api/v1/child/companion/photos/${id}`, { method: 'DELETE' });
  },
  phrasesList() {
    return call<{ phrases: Array<{ id: number; phrase: string; taught_at: number }> }>(
      '/api/v1/child/companion/phrases');
  },
  phraseAdd(phrase: string) {
    return call<{ ok: true; id: number; taught_at: number }>(
      '/api/v1/child/companion/phrases',
      { method: 'POST', body: JSON.stringify({ phrase }) });
  },
  phraseRemove(id: number) {
    return call<{ ok: true }>(`/api/v1/child/companion/phrases/${id}`, { method: 'DELETE' });
  },
  wishLetter() {
    return call<{
      sleeping: boolean;
      letter: {
        id: number; blessings: string[]; created_at: number; opened_at: number | null;
      } | null;
      reason?: string;
    }>(`/api/v1/child/companion/wish-letter?lang=${getLang()}`);
  },
  wishLetterOpen(id: number) {
    return call<{ ok: true }>(
      `/api/v1/child/companion/wish-letter/${id}/open`, { method: 'POST' });
  },
  submitVoiceBottle(
    transcript: string,
    durationMs: number,
    audioB64?: string,
    mime?: string,
  ) {
    return call<{ ok: true; ack: string; backup_id: number | null }>(
      '/api/v1/child/companion/voice-bottle',
      {
        method: 'POST',
        body: JSON.stringify({
          transcript,
          duration_ms: Math.round(durationMs),
          audio_b64: audioB64 || undefined,
          mime: mime || undefined,
          lang: getLang(),
        }),
      });
  },
  listVoiceBottles() {
    return call<{
      bottles: Array<{
        id: number; transcript: string; duration_ms: number;
        mime: string; audio_b64: string; created_at: number;
      }>;
    }>('/api/v1/child/companion/voice-bottles');
  },

  // ─── Onboarding hatching flow (v0.9.0 PR-1) ─────────────────────────────
  onboardingStatus() {
    return call<{
      first_pet_hatched: 0 | 1;
      hatched_at: number | null;
      egg_taps: number;
      onboarding_quiz_completed: 0 | 1;
    }>('/api/v1/child/onboarding/status');
  },
  onboardingStartHatch(taps: number) {
    return call<{ ok: true; egg_taps: number; ready_for_hatch?: boolean; ready_for_quiz: boolean }>(
      '/api/v1/child/onboarding/start-hatch',
      { method: 'POST', body: JSON.stringify({ taps }) },
    );
  },
  onboardingCompleteHatch(nickname: string) {
    return call<{
      ok: true;
      pet: {
        id: number; breed_id: number;
        nickname: string; species: string; breed_name: string;
        state: { hunger: number; happiness: number; level: number; exp: number; mood: string };
      };
    }>(
      '/api/v1/child/onboarding/complete-hatch',
      { method: 'POST', body: JSON.stringify({ nickname }) },
    );
  },
};
